import React, { useEffect } from 'react'
import { wealthboxSettingsDefaultValues, type WealthboxSettingsFormValues } from './models'
import { useWealthboxContactSync } from './hooks'
import { MultiSelectDropdown } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { SelectDropdown, type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import TagsInput from 'react-tagsinput'
import { Button } from '../../../../../components/Button/Button'

export const WealthboxContactSyncSettings = () => {
  const { autoSync, serviceConfig, tierTagSelectOptions, isSyncDisabled, isAccountFetching, isFetching, isSaving, save } = useWealthboxContactSync()

  const form = useForm<WealthboxSettingsFormValues>({ defaultValues: wealthboxSettingsDefaultValues })

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  useEffect(() => {
    if (serviceConfig?.inbound?.contact?.filters) {
      form.setValue('inbound.contact.filters', serviceConfig.inbound.contact.filters)
      form.setValue('inbound.contact.filters.tags', serviceConfig.inbound.contact.filters.tags ?? [])
    }
    if (serviceConfig?.inbound?.contact?.tierTags) {
      form.setValue('inbound.contact.tierTags', serviceConfig.inbound.contact.tierTags)
    }
  }, [serviceConfig, form])

  const onSubmit = async (data: WealthboxSettingsFormValues) => {
    await save(data)
  }

  const handleCancel = () => {
    form.reset()
  }

  const statusSelectOptions: Array<SelectOption> = [
    { id: '', label: '<no filter>' },
    { id: 'true', label: 'Active' },
    { id: 'false', label: 'Inactive' },
  ]

  const statusToSelectOption = (value?: boolean): SelectOption => {
    if (value === true) {
      return statusSelectOptions[1]
    }
    if (value === false) {
      return statusSelectOptions[2]
    }
    return statusSelectOptions[0]
  }

  const selectOptionToStatus = (selectOption: SelectOption): boolean | null => {
    if (selectOption.id === 'true') {
      return true
    }
    if (selectOption.id === 'false') {
      return false
    }
    return null
  }

  const typeSelectOptions: Array<SelectOption> = [
    { id: '', label: '<no filter>' },
    { id: 'person', label: 'Person' },
    { id: 'household', label: 'Household' },
    { id: 'organization', label: 'Organization' },
    { id: 'trust', label: 'Trust' },
  ]

  const typeToSelectOption = (value?: string): SelectOption => {
    if (value === 'person') {
      return typeSelectOptions[1]
    }
    if (value === 'household') {
      return typeSelectOptions[2]
    }
    if (value === 'organization') {
      return typeSelectOptions[3]
    }
    if (value === 'trust') {
      return typeSelectOptions[4]
    }
    return typeSelectOptions[0]
  }

  const selectOptionToType = (selectOption: SelectOption): string | null => {
    if (selectOption.id === '') {
      return null
    }
    return selectOption.id
  }

  const ContactTypeTooltip: React.FC = () => (
    <div className='m-2'>
      <b className='mb-2 d-block'>Possible default values are:</b>
      <ul className='m-0 ps-3 text-start'>
        <li>
          <b>Client</b>
        </li>
        <li>
          <b>Past Client</b>
        </li>
        <li>
          <b>Prospect</b>
        </li>
        <li>
          <b>Vendor</b>
        </li>
        <li>
          <b>Organization</b>
        </li>
      </ul>
    </div>
  )

  if (isFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and Wealthbox.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Filters</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div className='d-flex gap-2'>
            <div>Type</div>
            <Tooltip placement='top' id='wealthbox-type-tooltip' className='bold' title='The type of contact that should be synced'>
              <div className='align-self-center'>
                <i className='bi bi-info-circle-fill' />
              </div>
            </Tooltip>
          </div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.type'}
              control={form.control}
              render={({ field }) => (
                <SelectDropdown
                  className='py-2 zocks-px-0.75'
                  options={typeSelectOptions}
                  initialSelectedOption={typeToSelectOption(field.value)}
                  onOptionSelected={(s) => field.onChange(selectOptionToType(s))}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Status</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.active'}
              control={form.control}
              render={({ field }) => (
                <SelectDropdown
                  className='py-2 zocks-px-0.75'
                  options={statusSelectOptions}
                  initialSelectedOption={statusToSelectOption(field.value)}
                  onOptionSelected={(s) => field.onChange(selectOptionToStatus(s))}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div className='d-flex gap-2'>
            <div>Contact Type</div>
            <Tooltip placement='top' id='wealthbox-contact-type-tooltip' tooltipContent={<ContactTypeTooltip />}>
              <div className='align-self-center'>
                <i className='bi bi-info-circle-fill' />
              </div>
            </Tooltip>
          </div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.contactType'}
              control={form.control}
              render={({ field }) => <Form.Control id={field.name} type='text' {...field} />}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Tags</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.tags'}
              control={form.control}
              render={({ field }) => <TagsInput value={field.value ?? []} onChange={field.onChange} />}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Tiers</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Tags</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.tierTags'}
              control={form.control}
              render={({ field }) => (
                <MultiSelectDropdown
                  name={field.name}
                  options={tierTagSelectOptions ?? []}
                  initialSelectedOptions={field.value ?? []}
                  onSelectedChanged={field.onChange}
                  showSelectAllOption={true}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isSaving}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isSaving}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
